import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { TopStatusBar, YadaContainer, AppHeader, TextInputRow, NavigationButtons } from '@yada/ui';
import { Settings as SettingsIcon, Bell, MessageSquare } from 'lucide-react';
import type { AppManifest, AutocompleteFn, UiSlots, UndoRedoState, Command } from '@yada/types';
import { INSTANCE_CHANGED_EVENT, isLegacyStrongAnonymousProfile, validateAiToolArguments } from '@yada/types';
import { createShellRuntime } from '../bootstrap/createRuntime';
import {
  ALL_MANIFESTS,
  homeManifest,
  habitsManifest,
  mealPlannerManifest,
  docsManifest,
  calendarManifest,
  eventsManifest,
  tripsManifest,
  communityVotesManifest,
  chatManifest,
  trainingManifest,
  contactsManifest,
  financeManifest,
  shoppingListManifest,
  todoManifest,
  groupsManifest,
  contentDistributionManifest,
  emojiManifest,
  adminManifest,
  devtoolsManifest,
} from '../registry';

const MANIFEST_BY_ID = new Map(ALL_MANIFESTS.map(m => [m.id, m]));

import { ToolSwitcher } from './ToolSwitcher';
import { ToastHost } from './ToastHost';
import { ProfileSwitcher } from '../accounts/ProfileSwitcher';
import { ProfileButton } from '../accounts/ProfileButton';
import { AuthGuard } from '../auth/AuthGuard';
import { PendingDevicesProvider } from '../auth/PendingDevicesContext';
import { DeviceSyncStatus } from './DeviceSyncStatus';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { migrateLegacyWorkspaceId, getCurrentWorkspaceId as getCtxWorkspaceId } from '../workspace/workspaceContext';
import { getAppOwners, addAppOwner, setAppWorkspaceOverride } from '../workspace/appOwnership';
import { DmInvitationsProvider, useDmInvitations } from '../workspace/DmInvitationsContext';
import { useChatUnreadCount } from '../notifications/useChatUnreadCount';
import { LlmConsentProvider } from '../llm/LlmConsentContext';
import { LlmAgentProvider, useLlmAgent } from '../llm/LlmAgentContext';
import { SuggestionBar } from '../llm/SuggestionBar';
import { AiConversationDrawer } from '../llm/AiConversationDrawer';
import { LlmProviderIndicator } from '../llm/LlmProviderIndicator';
import { AccountProvider, useAccount } from '../context/AccountContext';
import { AppRegistryProvider, useAppRegistry } from '../context/AppRegistryContext';
import { ThemeProvider } from '../context/ThemeContext';
import { FontSizeProvider } from '../context/FontSizeContext';
import { CountryProvider } from '@yada/i18n';
import { startSyncPush, stopSyncPush, onRemoteDocumentUpdate, onPresenceChange } from '@yada/sync';
import { fireRemoteUpdateCallbacksForDocument } from '@yada/storage';
import { setRuntime } from '../settings/runtimeRef';
import { WidgetTray } from '../widgets/WidgetTray';
import { SafeFeatureBoundary } from '../components/SafeFeatureBoundary';

const Settings = React.lazy(() => import('../settings/Settings').then(m => ({ default: m.Settings })));
const WelcomeLanding = React.lazy(() => import('./WelcomeLanding').then(m => ({ default: m.WelcomeLanding })));
const PrivacyPolicy = React.lazy(() => import('../legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = React.lazy(() => import('../legal/TermsOfService').then(m => ({ default: m.TermsOfService })));
const Manifesto = React.lazy(() => import('../legal/Manifesto').then(m => ({ default: m.Manifesto })));
const AuthCallbackPage = React.lazy(() => import('../auth/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));
const TotpLoginPage = React.lazy(() => import('../auth/TotpLoginPage').then(m => ({ default: m.TotpLoginPage })));
const PairConfirmationModal = React.lazy(() => import('../auth/PairConfirmationModal').then(m => ({ default: m.PairConfirmationModal })));
const ChatsPanel = React.lazy(() => import('../workspace/DmInvitationsPanel').then(m => ({ default: m.ChatsPanel })));
const NotificationHistoryPanel = React.lazy(() => import('../notifications/NotificationHistoryPanel').then(m => ({ default: m.NotificationHistoryPanel })));
const ProfileSwitchToolDialog = React.lazy(() => import('../accounts/ProfileSwitchToolDialog').then(m => ({ default: m.ProfileSwitchToolDialog })));
import { ProfileProvider, useProfileContext } from '../context/ProfileContext';
import { setActiveProfile } from '@yada/storage';
import { prefetchApps } from '../utils/prefetchApps';

const runtime = createShellRuntime();

// Expose runtime singleton for settings sections (e.g., ExportSection)
setRuntime(runtime);

// Restore any notifications that were scheduled before the last page load
(runtime as any).notificationManager?.restore?.();

// Request persistent storage so the browser won't evict OPFS data without user action
navigator.storage?.persist?.();

// Register service worker (phase 1: enables notification clicks; phase 2: push)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch((err) => {
    console.warn('[YADA] Service worker registration failed:', err);
  });

  // Forward NOTIFICATION_ACTIVATE messages from SW to the runtime event bus.
  // When the user taps a push notification, the SW posts this message so the
  // correct plugin app can navigate to the relevant context.
  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data;
    if (!msg || msg.type !== 'NOTIFICATION_ACTIVATE') return;
    // Emit on the runtime bus — the active app's onActivate listeners will pick it up
    runtime.emit('notification:activate', {
      appId: msg.appId,
      notificationId: msg.notificationId,
      actionId: msg.actionId,
      data: msg.data,
    });
    // Also navigate to the target URL if provided
    if (msg.url && msg.url !== window.location.pathname) {
      window.history.pushState(null, '', msg.url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  });
}

// Register all non-privileged apps at startup.
// Privileged apps (admin, devtools) are registered conditionally in AppContent.
ALL_MANIFESTS.forEach(manifest => {
  if (manifest.id !== adminManifest.id && manifest.id !== devtoolsManifest.id) {
    runtime.register(manifest);
  }
});

// Conditionally register devtools app based on account isDeveloper flag
// This will be registered dynamically after account context loads in AppContent
// The registration happens in AppContent's useEffect to ensure account is available

const COMMAND_HISTORY_KEY = 'yada:command-history';
const MAX_HISTORY = 100;
const LAST_APP_PATHS_KEY = 'yada:last-app-paths';
function loadCommandHistory(): string[] {
    try {
        const stored = localStorage.getItem(COMMAND_HISTORY_KEY);
        return stored ? JSON.parse(stored) : [];
    }
    catch {
        return [];
    }
}
function saveCommandHistory(history: string[]) {
    try {
        localStorage.setItem(COMMAND_HISTORY_KEY, JSON.stringify(history.slice(-MAX_HISTORY)));
    }
    catch {
        // Ignore storage errors
    }
}
function loadLastAppPaths(): Record<string, string> {
    try {
        const stored = localStorage.getItem(LAST_APP_PATHS_KEY);
        return stored ? JSON.parse(stored) : {};
    }
    catch {
        return {};
    }
}
function saveLastAppPaths(paths: Record<string, string>) {
    try {
        localStorage.setItem(LAST_APP_PATHS_KEY, JSON.stringify(paths));
    }
    catch {
        // Ignore storage errors
    }
}
function getLastPathForApp(appId: string): string | null {
    const paths = loadLastAppPaths();
    return paths[appId] || null;
}
function setLastPathForApp(appId: string, path: string | null) {
    const paths = loadLastAppPaths();
    if (path !== null) {
        paths[appId] = path;
    }
    saveLastAppPaths(paths);
}
const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { badgeCount, pendingGroupInvitationCount } = useDmInvitations();
    const { unreadCount: chatUnreadCount } = useChatUnreadCount(runtime);
    const {
      isLoading: llmLoading,
      inConversationMode,
      startConversation,
      endConversation,
      sessionMessages,
      setIsConvoOpen,
    } = useLlmAgent();
    const { account } = useAccount();

    // ── Per-profile state (from shared ProfileContext) ──────────────────────
    const { activeProfile, profiles: allProfiles } = useProfileContext();

    // ── Profile switch dialog state ─────────────────────────────────────────
    const [profileSwitchPending, setProfileSwitchPending] = React.useState<{
      toProfileId: string;
      toProfileName: string;
      activeAppId: string;
      activeAppName: string;
    } | null>(null);

    // Conditionally register devtools app for developer accounts
    React.useEffect(() => {
      if (account?.isDeveloper) {
        // Only register if not already registered
        const alreadyRegistered = runtime.list().some(app => app.id === devtoolsManifest.id);
        if (!alreadyRegistered) {
          runtime.register(devtoolsManifest as AppManifest);
        }
      }
    }, [account?.isDeveloper]);

    // Conditionally register admin app for admin accounts
    React.useEffect(() => {
      if (account?.role === 'admin') {
        const alreadyRegistered = runtime.list().some(app => app.id === adminManifest.id);
        if (!alreadyRegistered) {
          runtime.register(adminManifest as AppManifest);
        }
      }
    }, [account?.role]);

    // App registry: provided by AppRegistryContext (fetched once on login)
    const { registry: appRegistry } = useAppRegistry();

    const visibleApps = React.useMemo(() => {
      const allApps = runtime.list(account?.isDeveloper);
      if (account === null) return allApps; // not yet loaded — show everything to avoid flash

      // Filter out developer-only apps for non-developer accounts
      const availableApps = account.isDeveloper
        ? allApps
        : allApps.filter(app => !app.developerOnly);

      // Per-profile enabled apps override account-level if set in metadata.
      // This lets a "gaming" profile hide meal planner etc. without affecting others.
      const profileEnabledApps = activeProfile?.metadata?.enabledApps as string[] | undefined;
      const enabledApps = profileEnabledApps ?? account.enabledApps;

      const isAdminOrDev = account.role === 'admin' || account.isDeveloper;

      // Home is always first; then apps in enabledApps order
      const result = [homeManifest.id, ...enabledApps]
        .filter((id, i, arr) => arr.indexOf(id) === i) // dedupe
        .map(id => availableApps.find(a => a.id === id))
        .filter((a): a is NonNullable<typeof a> => a != null)
        .filter(app => {
          // Admin and developers see all apps regardless of registry flags
          if (isAdminOrDev) return true;
          // Check app registry: hide if disabled or unreleased
          const regEntry = appRegistry.find(r => r.appId === app.id);
          if (!regEntry) return true; // not in registry → show by default
          
          if (account.role === 'tester') {
            return regEntry.enabled && (regEntry.releaseState === 'released' || regEntry.releaseState === 'pre_released');
          }
          
          return regEntry.enabled && regEntry.releaseState === 'released';
        });

      // Admin panel is always visible for admin users (not gated by enabledApps)
      if (account.role === 'admin') {
        const adminApp = availableApps.find(a => a.id === adminManifest.id);
        if (adminApp && !result.some(a => a.id === adminApp.id)) {
          result.push(adminApp);
        }
      }

      return result;
    }, [account, activeProfile, appRegistry]);

    const pinnedIds = React.useMemo(
      () => (activeProfile?.metadata?.pinnedApps as string[] | undefined) ?? account?.pinnedApps ?? [],
      [account, activeProfile]
    );
    const [pendingInputValue, setPendingInputValue] = React.useState<string | undefined>(undefined);
    const [showInvitationsPanel, setShowInvitationsPanel] = React.useState(false);
    const [showNotificationPanel, setShowNotificationPanel] = React.useState(false);
    const [notifUnreadCount, setNotifUnreadCount] = React.useState(() => runtime.notificationManager.unreadCount);
    const [isPomodoroActive, setIsPomodoroActive] = React.useState(false);

    React.useEffect(() => {
      import('@yada/app-calendar').then((m) => {
        if (m.pomodoroStore) {
          const update = (s: { mode: string }) => setIsPomodoroActive(s.mode === 'running' || s.mode === 'paused' || s.mode === 'finished');
          update(m.pomodoroStore.getState());
          return m.pomodoroStore.subscribe(update);
        }
      }).catch(() => {});
    }, []);

    // Keep unread badge count in sync with notification manager
    React.useEffect(() => {
      setNotifUnreadCount(runtime.notificationManager.unreadCount);
      return runtime.notificationManager.onHistoryChange(() => {
        setNotifUnreadCount(runtime.notificationManager.unreadCount);
      });
    }, []);

    const [activePairPayload, setActivePairPayload] = React.useState<string | null>(null);

    // Detect /pair deep-links or ?pair= queries from scanned QR codes
    React.useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const pairParam = searchParams.get('p') || searchParams.get('pair');
      if (pairParam) {
        setActivePairPayload(pairParam);
      }
    }, [location.pathname, location.search]);

    const [title, setTitle] = React.useState('YADA — Yet Another Data App');
    const [slots, setSlots] = React.useState<Partial<UiSlots>>({});
    const [activeId, setActiveId] = React.useState<string | undefined>(undefined);
    const [commandHistory, setCommandHistory] = React.useState<string[]>(loadCommandHistory());
    const [autocompleteFn, setAutocompleteFn] = React.useState<AutocompleteFn | null>(null);
    const [commands, setCommands] = React.useState<Command[]>([]);
    const [inputPlaceholder, setInputPlaceholder] = React.useState<string | undefined>(undefined);
    const [undoRedoState, setUndoRedoState] = React.useState<UndoRedoState | null>(null);
    const updateAutocomplete = React.useCallback(() => {
        // Get autocomplete function and commands from active instance
        const instance = runtime.active?.instance;
        if (instance?.getAutocomplete) {
            const fn = instance.getAutocomplete();
            setAutocompleteFn(() => fn);
        }
        else {
            setAutocompleteFn(null);
        }
        setCommands(instance?.commands ?? []);
        setInputPlaceholder(instance?.inputPlaceholder);
        setUndoRedoState(instance?.undoRedoState ?? null);
    }, []);
    // Save current path periodically for docs app (to catch internal selection changes)
    React.useEffect(() => {
        if (activeId !== docsManifest.id)
            return;
        const saveCurrentPath = () => {
            const instance = runtime.active?.instance;
            if (instance?.getCurrentPath) {
                const currentPath = instance.getCurrentPath();
                if (currentPath !== null && currentPath !== undefined) {
                    setLastPathForApp(docsManifest.id, currentPath);
                }
            }
        };
        saveCurrentPath();
        return runtime.on(INSTANCE_CHANGED_EVENT, saveCurrentPath);
    }, [activeId]);

    const doProfileSwitch = React.useCallback(async (profileId: string) => {
      await setActiveProfile(profileId);
      // Track the last personal profile so social apps can auto-switch back to it
      const profile = allProfiles.find(p => p.id === profileId);
      if (profile && profile.profileType !== 'internal' && profile.profileType !== 'household') {
        localStorage.setItem('yada:account:lastNonInternalProfileId', profileId);
      }
      window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId } }));
    }, [allProfiles]);

    // Sync URL to app activation
    React.useEffect(() => {
        void (async () => {
        const path = location.pathname;
        let targetAppId: string | null = null;
        let docsPath: string | null = null;
        if (path === '/about') {
            // About page - don't activate any app
            return;
        }
        else if (path === '/settings') {
            // Settings page - don't activate any app, just show the view
            return;
        }
        else if (path === '/habits') {
            targetAppId = habitsManifest.id;
        }
        else if (path === '/calendar') {
            targetAppId = calendarManifest.id;
        }
        else if (path === '/events' || path.startsWith('/events')) {
            targetAppId = eventsManifest.id;
        }
        else if (path === '/trips' || path.startsWith('/trips')) {
            targetAppId = tripsManifest.id;
        }
        else if (path === '/chat' || path.startsWith('/chat')) {
            targetAppId = chatManifest.id;
        }
        else if (path === '/meal-planner' || path.startsWith('/meal-planner')) {
            targetAppId = mealPlannerManifest.id;
        }
        else if (path === '/community-votes' || path.startsWith('/community-votes')) {
            targetAppId = communityVotesManifest.id;
        }
        else if (path.startsWith('/docs')) {
            targetAppId = docsManifest.id;
            // Extract path after /docs/
            const match = path.match(/^\/docs(?:\/(.*))?$/);
            docsPath = match && match[1] ? decodeURIComponent(match[1]) : null;
        }
        else if (path === '/training' || path.startsWith('/training')) {
            targetAppId = trainingManifest.id;
        }
        else if (path === '/contacts' || path.startsWith('/contacts')) {
            targetAppId = contactsManifest.id;
        }
        else if (path === '/finance' || path.startsWith('/finance')) {
            targetAppId = financeManifest.id;
        }
        else if (path === '/shopping-list' || path.startsWith('/shopping-list')) {
            targetAppId = shoppingListManifest.id;
        }
        else if (path === '/todo' || path.startsWith('/todo')) {
            targetAppId = todoManifest.id;
        }
        else if (path === '/groups' || path.startsWith('/groups')) {
            targetAppId = groupsManifest.id;
        }
        else if (path === '/publish' || path.startsWith('/publish')) {
            targetAppId = contentDistributionManifest.id;
        }
        else if (path === '/devtools' || path.startsWith('/devtools')) {
            targetAppId = devtoolsManifest.id;
        }
        else if (path === '/emoji' || path.startsWith('/emoji')) {
            targetAppId = emojiManifest.id;
        }
        else if (path === '/admin' || path.startsWith('/admin')) {
            // Guard: only admins can access /admin.
            // account === null means AccountContext is still loading — skip for now;
            // useAdminGuard() inside AdminMain will redirect once the account is ready.
            if (account !== null && account.role !== 'admin') {
                navigate('/home', { replace: true });
                return;
            }
            targetAppId = adminManifest.id;
        }
        else if (path === '/home') {
            targetAppId = homeManifest.id;
        }
        else if (path === '/' || path === '') {
            // Default to home
            targetAppId = homeManifest.id;
            navigate('/home', { replace: true });
            return;
        }
        // Events and Trips support standalone local planning. Their optional
        // invitation and group features handle their own authenticated flows,
        // so only apps that are inherently social are gated here.
        const SOCIAL_APPS = [chatManifest.id, contactsManifest.id, groupsManifest.id, communityVotesManifest.id];
        const isAccountless = account?.isAccountless;
        const isUnconfirmed = account?.emailVerified === false;

        if (targetAppId && SOCIAL_APPS.includes(targetAppId) && isAccountless) {
          console.log('[App] Redirecting social app request to home (Accountless Mode)');
          navigate('/home', { replace: true });
          return;
        }

        if (targetAppId && SOCIAL_APPS.includes(targetAppId) && isUnconfirmed) {
          console.log('[App] Redirecting social app request to home (Unconfirmed Email)');
          navigate('/home', { replace: true });
          runtime.emit('toast', 'Please confirm your email address to access social features');
          return;
        }

        // Internal and household profiles cannot be the active sender for social apps.
        // Auto-switch to the last-used personal profile (or first standard).
        if (targetAppId && SOCIAL_APPS.includes(targetAppId) && (activeProfile?.profileType === 'internal' || activeProfile?.profileType === 'household')) {
          const fallbackId = localStorage.getItem('yada:account:lastNonInternalProfileId');
          const fallback = (fallbackId ? allProfiles.find(p => p.id === fallbackId && p.profileType !== 'household' && p.profileType !== 'internal') : null)
            ?? allProfiles.find(p => p.profileType === 'standard')
            ?? allProfiles.find(p => p.profileType !== 'internal' && p.profileType !== 'household' && !isLegacyStrongAnonymousProfile(p))
            ?? allProfiles[0];
          if (fallback) {
            console.log('[App] Auto-switching from profile to', fallback.name, 'for social app');
            await doProfileSwitch(fallback.id);
            runtime.emit('toast', `Switched to ${fallback.name} — chat isn't available on your ${activeProfile?.profileType === 'household' ? 'Household' : 'Internal'} profile`);
          } else {
            // No viable public profile — redirect home
            navigate('/home', { replace: true });
            runtime.emit('toast', 'Create a personal profile to use Chat');
            return;
          }
        }

        if (targetAppId && targetAppId !== activeId) {
            // Before switching, save current app's path if it's docs
            if (activeId === docsManifest.id) {
                const instance = runtime.active?.instance;
                if (instance?.getCurrentPath) {
                    const currentPath = instance.getCurrentPath();
                    setLastPathForApp(docsManifest.id, currentPath);
                }
                else {
                    // Fallback to URL if instance not available
                    const match = location.pathname.match(/^\/docs(?:\/(.*))?$/);
                    const currentPath = match && match[1] ? decodeURIComponent(match[1]) : '';
                    setLastPathForApp(docsManifest.id, currentPath);
                }
            }
            runtime.activate(targetAppId).then(async () => {
                setActiveId(targetAppId ?? undefined);
                updateAutocomplete();
                // If docs app, select file/folder based on path
                if (targetAppId === docsManifest.id) {
                    const instance = runtime.active?.instance;
                    if (instance?.selectByPath) {
                        let pathToSelect;
                        if (docsPath !== null) {
                            // URL has explicit path - use it
                            pathToSelect = docsPath;
                        }
                        else {
                            // No path in URL - restore last saved path
                            const lastPath = getLastPathForApp(docsManifest.id);
                            pathToSelect = lastPath || '';
                            // Update URL to reflect restored path
                            if (lastPath) {
                                navigate(`/docs/${encodeURIComponent(lastPath)}`, { replace: true });
                                // The navigation will trigger this effect again with the new path
                                // So we'll select it in the next run - no need to select here
                                return;
                            }
                        }
                        await instance.selectByPath(pathToSelect);
                    }
                }
            });
        }
        else if (targetAppId === docsManifest.id && activeId === docsManifest.id) {
            // Same app, but path changed - update selection
            const instance = runtime.active?.instance;
            if (instance?.selectByPath) {
                const pathToSelect = docsPath !== null ? docsPath : '';
                instance.selectByPath(pathToSelect).catch((err: unknown) => {
                    console.error('Failed to select by path:', err);
                });
            }
        }
        })();
    }, [location.pathname, navigate, updateAutocomplete, activeId, activeProfile, allProfiles, account, doProfileSwitch]);
    React.useEffect(() => {
        const offTitle = runtime.on('title', (t) => setTitle(String(t)));
        const offToast = runtime.on('toast', (m) => {
            // naive toast for now
            console.log('[toast]', m);
        });
        const offSlots = runtime.on('slots', (s) => setSlots(s));
        const offNavigate = runtime.on('shell:navigate', (path) => navigate(path as string));
        // Listen for profile creation events from tools
        const handleCreateHousehold = async () => {
            const { createHouseholdProfile } = await import('../accounts/useHouseholdProfile');
            try {
                await createHouseholdProfile();
                // Refresh profile switcher if it exists
                runtime.emit('profile:refresh');
            }
            catch (err) {
                console.error('Failed to create household profile:', err);
                // Navigate to account management as fallback
                navigate('/settings?section=profiles');
            }
        };
        const offCreateHousehold = runtime.on('profile:create-household', handleCreateHousehold);
        const offInviteContact = runtime.on('profile:invite-contact', async (payload: any) => {
            const { email, contactName } = payload as { email: string; contactName?: string };
            try {
                const { fetchWithAuth } = await import('@yada/api-client');
                const { AUTH_API_URL } = await import('../config/api');
                const res = await fetchWithAuth(`${AUTH_API_URL}/api/invitations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ target_email: email }),
                });

                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.message || body.error || 'Failed to create invitation');
                }

                const inv = await res.json();
                const inviteUrl = `${window.location.origin}/?invite=${inv.code}`;
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(inviteUrl);
                    runtime.emit('toast', `Invite created & link copied for ${contactName ?? email}`);
                } else {
                    runtime.emit('toast', `Invite created for ${contactName ?? email}`);
                }
            } catch (err) {
                console.error('Failed to create invitation:', err);
                navigate(`/settings?section=invitations`);
            }
        });
        // Apps announce changes to autocomplete, commands and undo/redo state.
        const offInstanceChanged = runtime.on(INSTANCE_CHANGED_EVENT, updateAutocomplete);
        return () => {
            offTitle();
            offToast();
            offSlots();
            offNavigate();
            offCreateHousehold();
            offInviteContact();
            offInstanceChanged();
        };
    }, [updateAutocomplete, navigate]);
    const handleCommand = async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        // Add to history (avoid duplicates)
        setCommandHistory((prev: string[]) => {
            const filtered = prev.filter((cmd: string) => cmd !== trimmed);
            const updated = [...filtered, trimmed];
            saveCommandHistory(updated);
            return updated;
        });

        // 1. Check for AI conversation exit commands
        const EXIT_COMMANDS = ['ai end', 'ai exit', 'ai quit', 'ai bye', 'exit', 'quit'];
        if (inConversationMode && EXIT_COMMANDS.includes(trimmed.toLowerCase())) {
            endConversation();
            runtime.emit('toast', 'Exited AI conversation mode');
            return;
        }

        // 2. Check if this is an AI prompt:
        // Either starting with case-insensitive "ai " / "ai\n" prefix, or we are already in conversation mode.
        const aiPrefixMatch = trimmed.match(/^(ai|AI|Ai|aI)[\s\n]+([\s\S]*)$/);
        const isAiTrigger = Boolean(aiPrefixMatch) || inConversationMode;

        if (isAiTrigger) {
            // Extract the actual prompt content
            const promptContent = aiPrefixMatch ? aiPrefixMatch[2].trim() : trimmed;

            if (!promptContent) {
                // If user just typed "ai", enter conversation mode
                startConversation();
                runtime.emit('toast', 'AI Conversation mode active. Type "ai end" or "exit" to quit.');
                return;
            }

            if (!runtime.llmManager.getProvider()) {
                runtime.emit('toast', 'No AI provider configured — set one up in Settings → AI');
                return;
            }

            // Enter conversation mode for continuous turns
            startConversation();

            const activeAppId = runtime.active?.id || '@yada/app-home';
            const activeAppName = MANIFEST_BY_ID.get(activeAppId)?.name || 'YADA';

            // Collect dynamic AI context, tools, and apply callback from active app
            const socialApps = [chatManifest.id, contactsManifest.id, groupsManifest.id, communityVotesManifest.id];
            const restrictedProfile = activeProfile?.profileType === 'internal' || activeProfile?.profileType === 'household';
            const agentAppIds = visibleApps.map(app => app.id).filter(id => !restrictedProfile || !socialApps.includes(id));
            const requestInstance = runtime.active?.instance;
            const currentScope = () => JSON.stringify([
                localStorage.getItem('yada:account:activeAccountId') ?? account?.id,
                localStorage.getItem('yada:account:activeProfileId') ?? activeProfile?.id,
                getCtxWorkspaceId(),
            ]);
            // Capture the actual persisted scope as well as the app instance.
            const persistedScope = currentScope();
            const aiContext = await runtime.getAgentContext(activeAppId, agentAppIds);
            if (currentScope() !== persistedScope || runtime.active?.instance !== requestInstance) {
                runtime.emit('toast', 'The profile, workspace, or active app changed while preparing this request. Submit it again.');
                return;
            }

            // Build dynamic system prompt with tool context and designated tools
            const { getSystemPromptForApp } = await import('../llm/appPrompts');
            let extraInstruction = '';
            // LlmManager supplies a fresh app/view snapshot for every model call.
            if (aiContext?.systemInstruction) {
                extraInstruction += `${aiContext.systemInstruction}\n\n`;
            }

            const systemPrompt = getSystemPromptForApp(activeAppId, extraInstruction.trim() || undefined, aiContext?.tools);

            // Construct multi-turn conversation messages
            const messages: import('@yada/types').LlmMessage[] = [
                { role: 'system', content: systemPrompt },
            ];

            // Append past session messages for context
            for (const msg of sessionMessages.slice(-8)) {
                if (msg.kind === 'receipt') {
                    messages.push({ role: 'user', content: msg.content });
                } else if (msg.role === 'user' || msg.role === 'assistant') {
                    messages.push({ role: msg.role, content: msg.content });
                }
            }

            messages.push({ role: 'user', content: promptContent });

            const tools = aiContext?.tools;
            const executeTool = async (toolName: string, args: Record<string, unknown>) => {
                if (currentScope() !== persistedScope || runtime.active?.instance !== requestInstance) {
                    return { success: false, message: 'The profile, workspace, or active app changed. Submit this request again in the intended scope.' };
                }
                const targetTool = tools?.find(tool => tool.name === toolName);
                if (!targetTool) {
                    return { success: false, message: `The active app does not provide a tool named "${toolName}".` };
                }
                const validationError = validateAiToolArguments(targetTool, args);
                if (validationError) return { success: false, message: validationError };
                try {
                    return await targetTool.handler(args);
                } catch (err: any) {
                    console.error('[handleCommand] Tool execution failed:', err);
                    return { success: false, message: err?.message || String(err) };
                }
            };
            const requiresToolConfirmation = (toolName: string) =>
                Boolean(tools?.find(tool => tool.name === toolName) && tools.find(tool => tool.name === toolName)?.requiresConfirmation !== false);

            // Prose is an answer/draft, never an apply operation. Copy is explicit in the UI.
            const handleAccept = async () => {};

            await runtime.llmManager.invokeAgent(
                activeAppId,
                activeAppName,
                {
                    messages,
                    classification: 'personal-remote',
                    dataLabel: `${activeAppName} context`,
                },
                handleAccept,
                tools?.length ? executeTool : undefined,
                requiresToolConfirmation,
            );
            return;
        }

        // 3. Normal app command routing (no AI prefix and not in conversation mode)
        runtime.submitCommand(trimmed);
    };
    const handleManageProfiles = () => {
        navigate('/settings?section=profiles');
    };

    // ── Profile switch interception ─────────────────────────────────────────

    const handleProfileSwitchRequest = async (toProfileId: string) => {
      const activeAppId = runtime.active?.id;

      // No active tool, or home — switch without interruption
      if (!activeAppId || activeAppId === homeManifest.id) {
        await doProfileSwitch(toProfileId);
        return;
      }

      const toProfile = allProfiles.find(p => p.id === toProfileId);
      const activeApp = visibleApps.find(a => a.id === activeAppId);

      setProfileSwitchPending({
        toProfileId,
        toProfileName: toProfile?.name ?? 'the new profile',
        activeAppId,
        activeAppName: activeApp?.name ?? 'this tool',
      });
    };

    const handleSwitchFresh = async () => {
      if (!profileSwitchPending) return;
      const { toProfileId, activeAppId } = profileSwitchPending;
      setProfileSwitchPending(null);

      await doProfileSwitch(toProfileId);
      addAppOwner(activeAppId, toProfileId);
      // Re-mount the app so it initialises storage under the new profile
      runtime.activate(activeAppId).then(() => updateAutocomplete());
    };

    const handleShareData = async () => {
      if (!profileSwitchPending) return;
      const { toProfileId, activeAppId } = profileSwitchPending;
      setProfileSwitchPending(null);

      // Point the new profile's storage at the current profile's workspace
      const currentWorkspaceId = getCtxWorkspaceId();
      if (currentWorkspaceId) {
        setAppWorkspaceOverride(toProfileId, activeAppId, currentWorkspaceId);
      }

      await doProfileSwitch(toProfileId);
      addAppOwner(activeAppId, toProfileId);
      runtime.activate(activeAppId).then(() => updateAutocomplete());
    };
    const handleAppActivate = async (id: string) => {
        const SOCIAL_APPS = [chatManifest.id, contactsManifest.id, groupsManifest.id, communityVotesManifest.id];

        // ── Guard: social apps block internal and household profiles ───────────────
        if (SOCIAL_APPS.includes(id) && (activeProfile?.profileType === 'internal' || activeProfile?.profileType === 'household')) {
          const fallbackId = localStorage.getItem('yada:account:lastNonInternalProfileId');
          const fallback = (fallbackId ? allProfiles.find(p => p.id === fallbackId && p.profileType !== 'household' && p.profileType !== 'internal') : null)
            ?? allProfiles.find(p => p.profileType === 'standard')
            ?? allProfiles.find(p => p.profileType !== 'internal' && p.profileType !== 'household' && !isLegacyStrongAnonymousProfile(p))
            ?? allProfiles[0];
          if (fallback) {
            await doProfileSwitch(fallback.id);
            runtime.emit('toast', `Switched to ${fallback.name} — chat isn't available on your ${activeProfile?.profileType === 'household' ? 'Household' : 'Internal'} profile`);
          } else {
            navigate('/home', { replace: true });
            runtime.emit('toast', 'Create a personal profile to use Chat');
            return;
          }
        }

        // ── Auto-switch profile ─────────────────────────────────────────────
        // If exactly one OTHER profile owns this app and the current profile
        // hasn't used it yet, silently switch so the user lands on their data.
        if (id !== homeManifest.id) {
          const currentProfileId = localStorage.getItem('yada:account:activeProfileId');
          if (currentProfileId) {
            const owners = getAppOwners(id);
            if (!owners.includes(currentProfileId) && owners.length === 1 && allProfiles.some(p => p.id === owners[0])) {
              await setActiveProfile(owners[0]);
              window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: owners[0] } }));
            }
          }
        }

        // Save current app's path before switching
        if (activeId === docsManifest.id) {
            const instance = runtime.active?.instance;
            if (instance?.getCurrentPath) {
                const currentPath = instance.getCurrentPath();
                setLastPathForApp(docsManifest.id, currentPath);
            }
            else {
                // Fallback to URL if instance not available
                const match = location.pathname.match(/^\/docs(?:\/(.*))?$/);
                const currentPath = match && match[1] ? decodeURIComponent(match[1]) : '';
                setLastPathForApp(docsManifest.id, currentPath);
            }
        }
        setActiveId(id);
        // Update URL based on app
        if (id === habitsManifest.id) {
            navigate('/habits');
        }
        else if (id === calendarManifest.id) {
            navigate('/calendar');
        }
        else if (id === eventsManifest.id) {
            navigate('/events');
        }
        else if (id === tripsManifest.id) {
            navigate('/trips');
        }
        else if (id === chatManifest.id) {
            navigate('/chat');
        }
        else if (id === mealPlannerManifest.id) {
            navigate('/meal-planner');
        }
        else if (id === communityVotesManifest.id) {
            navigate('/community-votes');
        }
        else if (id === trainingManifest.id) {
            navigate('/training');
        }
        else if (id === contactsManifest.id) {
            navigate('/contacts');
        }
        else if (id === financeManifest.id) {
            navigate('/finance');
        }
        else if (id === shoppingListManifest.id) {
            navigate('/shopping-list');
        }
        else if (id === todoManifest.id) {
            navigate('/todo');
        }
        else if (id === groupsManifest.id) {
            navigate('/groups');
        }
        else if (id === contentDistributionManifest.id) {
            navigate('/publish');
        }
        else if (id === devtoolsManifest.id) {
            navigate('/devtools');
        }
        else if (id === emojiManifest.id) {
            navigate('/emoji');
        }
        else if (id === adminManifest.id) {
            navigate('/admin');
        }
        else if (id === homeManifest.id) {
            navigate('/home');
        }
        else if (id === docsManifest.id) {
            // Check for last saved path and navigate to it
            const lastPath = getLastPathForApp(docsManifest.id);
            if (lastPath) {
                navigate(`/docs/${encodeURIComponent(lastPath)}`);
            }
            else {
                navigate('/docs');
            }
        }
        else {
            // For other apps, use their ID as path
            navigate(`/${id}`);
        }
        runtime.activate(id).then(() => {
          updateAutocomplete();
          // Record that the current profile has used this app so future
          // navigations can auto-switch back to the right profile.
          if (id !== homeManifest.id) {
            const profileId = localStorage.getItem('yada:account:activeProfileId');
            if (profileId) addAppOwner(id, profileId);
          }
        });
    };
    // Settings takes over entire interface
    if (location.pathname === '/settings') {
        return (<div id="app-root">
        <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--muted)' }}>Loading...</div>}>
          <Settings />
        </React.Suspense>
        <ToastHost id="app-toast-host" bus={{ on: (e: string, cb: (payload: unknown) => void) => runtime.on(e, cb) }}/>
      </div>);
    }
    return (<div id="app-root">
      <TopStatusBar left={<>
          <NavigationButtons onUndo={undoRedoState?.undo} onRedo={undoRedoState?.redo} canUndo={undoRedoState?.canUndo ?? false} canRedo={undoRedoState?.canRedo ?? false} showUndoRedo={!!undoRedoState}/>
          {account?.isDeveloper && !isPomodoroActive && (
            <span
              className="nav-tag-badge nav-tag-badge--dev"
              title="Developer account - you have access to developer tools"
            >
              DEV
            </span>
          )}
          {account?.isAccountless && (
            <span
              className="nav-tag-badge nav-tag-badge--local"
              title="Local Mode - data is stored only on this device"
            >
              LOCAL
            </span>
          )}
        </>} right={<>
          <WidgetTray slot="navbar" />
          <SyncStatusIndicator />
          {/* Notification history bell */}
          <button
            id="app-notifications-btn"
            type="button"
            className="nav-action-btn"
            title={notifUnreadCount > 0 ? `${notifUnreadCount} unread notification${notifUnreadCount > 1 ? 's' : ''}` : 'Notification history'}
            onClick={() => setShowNotificationPanel((v) => !v)}
            aria-label={notifUnreadCount > 0 ? `${notifUnreadCount} unread notifications` : 'Notification history'}
          >
            <Bell size={16} />
            {notifUnreadCount > 0 && (
              <span className="nav-badge-count">
                {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
              </span>
            )}
          </button>
          {!account?.isAccountless && (
            <button
              id="app-chats-btn"
              type="button"
              className="nav-action-btn"
              title={
                chatUnreadCount > 0 && badgeCount > 0
                  ? `Chats — ${chatUnreadCount} unread message${chatUnreadCount > 1 ? 's' : ''}, ${badgeCount} request${badgeCount > 1 ? 's' : ''}`
                  : chatUnreadCount > 0
                    ? `Chats — ${chatUnreadCount} unread message${chatUnreadCount > 1 ? 's' : ''}`
                    : badgeCount > 0
                      ? `Chats — ${badgeCount} incoming request${badgeCount > 1 ? 's' : ''}`
                      : 'Chats'
              }
              aria-label={
                chatUnreadCount > 0 && badgeCount > 0
                  ? `Chats — ${chatUnreadCount} unread messages, ${badgeCount} requests`
                  : chatUnreadCount > 0
                    ? `Chats — ${chatUnreadCount} unread messages`
                    : badgeCount > 0
                      ? `Chats — ${badgeCount} requests`
                      : 'Chats'
              }
              onClick={() => setShowInvitationsPanel((v) => !v)}
            >
              <MessageSquare size={16} />
              {/* Unread messages badge — shown when there are unread chat messages */}
              {chatUnreadCount > 0 && (
                <span
                  aria-hidden="true"
                  className="nav-badge-count"
                >
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </span>
              )}
              {/* Invitation badge — shown only when no unread messages but invitations exist */}
              {chatUnreadCount === 0 && badgeCount > 0 && (
                <span
                  aria-hidden="true"
                  className="nav-badge-count nav-badge-count--muted"
                >
                  {badgeCount}
                </span>
              )}
            </button>
          )}
          <LlmProviderIndicator llmManager={runtime.llmManager} />
          <button
            id="app-settings-btn"
            type="button"
            className="nav-action-btn"
            onClick={() => navigate('/settings')}
            title={pendingGroupInvitationCount > 0 ? `${pendingGroupInvitationCount} pending workspace invitation${pendingGroupInvitationCount > 1 ? 's' : ''}` : 'Settings'}
            aria-label={pendingGroupInvitationCount > 0 ? `Settings — ${pendingGroupInvitationCount} pending workspace invitation${pendingGroupInvitationCount > 1 ? 's' : ''}` : 'Settings'}
          >
            <SettingsIcon size={16}/>
            {pendingGroupInvitationCount > 0 && (
              <span
                aria-hidden="true"
                className="nav-badge-count"
              >
                {pendingGroupInvitationCount > 9 ? '9+' : pendingGroupInvitationCount}
              </span>
            )}
          </button>
        </>}/>
      <YadaContainer mobileNavTrigger={activeId} defaultLeftOpen={MANIFEST_BY_ID.get(activeId ?? '')?.openDrawerByDefault ?? false} header={slots.header ? <AppHeader fullContent={slots.header as React.ReactNode}/> : <AppHeader title={title}/>} main={slots.main as React.ReactNode} leftSide={slots.leftDrawer as React.ReactNode} rightSide={<ToolSwitcher apps={visibleApps} activeId={activeId} onActivate={handleAppActivate} pinnedIds={pinnedIds}/>} rightSideFooter={<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}><div style={{ flex: 1, minWidth: 0 }}><ProfileSwitcher onManageProfiles={handleManageProfiles} onRequestSwitch={handleProfileSwitchRequest}/></div><DeviceSyncStatus /></div>} profileSlot={<ProfileButton onManageProfiles={handleManageProfiles} onRequestSwitch={handleProfileSwitchRequest}/>} input={slots.input ? slots.input as React.ReactNode : (<>
            <SuggestionBar onEdit={setPendingInputValue} />
            <AiConversationDrawer onEdit={setPendingInputValue} />
            <TextInputRow
              onSubmit={handleCommand}
              autocomplete={autocompleteFn || undefined}
              history={commandHistory}
              commands={commands}
              isLoading={llmLoading}
              pendingValue={pendingInputValue}
              onPendingValueConsumed={() => setPendingInputValue(undefined)}
              leadingAction={inConversationMode ? (
                <button
                  type="button"
                  onClick={() => setIsConvoOpen(true)}
                  aria-label="Open AI session history"
                  title="Open AI session history"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minHeight: 'var(--input-height, 2rem)', padding: '0 0.55rem', border: '1px solid var(--border)', borderRadius: 'var(--input-border-radius, 0.375rem)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  <MessageSquare size={15} /> AI ({sessionMessages.length})
                </button>
              ) : undefined}
              placeholder={
                inConversationMode
                  ? 'ai > Ask follow-up or type "ai end" to exit...'
                  : inputPlaceholder
              }
            />
          </>)}/>
      <ToastHost id="app-toast-host" bus={{ on: (e: string, cb: (payload: unknown) => void) => runtime.on(e, cb) }}/>
      <React.Suspense fallback={null}>
        {profileSwitchPending && (
          <ProfileSwitchToolDialog
            isOpen={profileSwitchPending !== null}
            toolName={profileSwitchPending?.activeAppName ?? ''}
            fromProfileName={activeProfile?.name ?? 'current profile'}
            toProfileName={profileSwitchPending?.toProfileName ?? ''}
            onStartFresh={handleSwitchFresh}
            onShareData={handleShareData}
            onCancel={() => setProfileSwitchPending(null)}
          />
        )}
        {showInvitationsPanel && (
          <SafeFeatureBoundary featureName="Chats & Messages">
            <ChatsPanel runtime={runtime} onClose={() => setShowInvitationsPanel(false)} />
          </SafeFeatureBoundary>
        )}
        {showNotificationPanel && (
          <SafeFeatureBoundary featureName="Notification History">
            <NotificationHistoryPanel
              manager={runtime.notificationManager}
              runtime={runtime}
              onClose={() => setShowNotificationPanel(false)}
            />
          </SafeFeatureBoundary>
        )}
        {activePairPayload && (
          <SafeFeatureBoundary featureName="Device Pairing">
            <PairConfirmationModal
              payload={activePairPayload}
              onClose={() => {
                setActivePairPayload(null);
                // Clean up URL query param if present
                if (location.pathname === '/pair' || location.search.includes('p=')) {
                  navigate('/home', { replace: true });
                }
              }}
            />
          </SafeFeatureBoundary>
        )}
      </React.Suspense>
    </div>);
};
export const App = () => {
    // Phase 2: Run migration from legacy activeWorkspaceId to new activeWorkspaces map
    useEffect(() => {
        migrateLegacyWorkspaceId();
    }, []);

    // Idle-prefetch all app bundles so they are cached locally for offline access
    useEffect(() => {
        return prefetchApps(ALL_MANIFESTS);
    }, []);

    // Start real-time SSE push client when workspace is authenticated.
    // When a remote document update arrives, broadcast it on the runtime event bus
    // so any app (e.g. shopping list) can react and refresh its UI.
    //
    // Also listens for yada:workspace:changed events to reconnect SSE when the
    // workspace context changes (e.g., after device approval completes).
    useEffect(() => {
        // Handle workspace becoming available — covers two cases:
        //   1. Workspace already in localStorage on mount (page reload / existing session)
        //   2. Workspace set after mount (fresh login on a new device)
        const startSyncForWorkspace = async (workspaceId: string) => {
            try {
                const { listWorkspaces } = await import('@yada/api-client');
                const workspaces = await listWorkspaces();
                const sharedWorkspaces = workspaces
                    .filter(w => w.workspace_type !== 'personal' && w.membership_status === 'active')
                    .map(w => w.id);
                await startSyncPush(workspaceId, sharedWorkspaces);
            } catch {
                await startSyncPush(workspaceId);
            }
        };

        const workspaceId = getCtxWorkspaceId();
        if (workspaceId) {
            void startSyncForWorkspace(workspaceId);
        }

        // Fresh login path: LoginScreen calls setCurrentWorkspaceId() after App
        // mounts, so the useEffect above misses it. Also handles workspace clear (logout).
        const handleWorkspaceActivated = (e: Event) => {
            const id = (e as CustomEvent<{ workspaceId: string | null }>).detail?.workspaceId;
            if (id) {
                startSyncForWorkspace(id);
            } else {
                stopSyncPush();
            }
        };
        window.addEventListener('yada:workspace:activated', handleWorkspaceActivated);

        // Forward SSE document updates to the runtime event bus so apps can react,
        // and also fire any onRemoteUpdate() callbacks registered for that document
        // directly — bypassing the Yjs doc.on('update') path which requires
        // _doInitialize() to have already run.
        const offUpdate = onRemoteDocumentUpdate((documentId: string) => {
            runtime.emit('sync:document-updated', { documentId });
            fireRemoteUpdateCallbacksForDocument(documentId);
        });
        // Forward presence changes so apps can show "N members online"
        const offPresence = onPresenceChange((members) => {
            runtime.emit('sync:presence-changed', { onlineCount: members.length, members });
        });
        return () => {
            window.removeEventListener('yada:workspace:activated', handleWorkspaceActivated);
            offUpdate();
            offPresence();
            stopSyncPush();
        };
    }, []);

    // Keep --viewport-height synchronized with mobile visualViewport and browser chrome
    useEffect(() => {
        const updateViewportHeight = () => {
            const height = window.visualViewport?.height ?? window.innerHeight;
            document.documentElement.style.setProperty('--viewport-height', `${height}px`);
            if (window.scrollY !== 0 || window.scrollX !== 0) {
                window.scrollTo(0, 0);
            }
            if (document.body.scrollTop !== 0 || document.body.scrollLeft !== 0) {
                document.body.scrollTop = 0;
                document.body.scrollLeft = 0;
            }
        };

        updateViewportHeight();

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateViewportHeight);
            window.visualViewport.addEventListener('scroll', updateViewportHeight);
        }
        window.addEventListener('resize', updateViewportHeight);
        window.addEventListener('orientationchange', updateViewportHeight);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', updateViewportHeight);
                window.visualViewport.removeEventListener('scroll', updateViewportHeight);
            }
            window.removeEventListener('resize', updateViewportHeight);
            window.removeEventListener('orientationchange', updateViewportHeight);
        };
    }, []);

    return (<BrowserRouter>
    <FontSizeProvider>
    <ThemeProvider>
    <CountryProvider>
    <AccountProvider>
    <AppRegistryProvider>
    <ProfileProvider>
    <AuthGuard>
      <PendingDevicesProvider>
        <DmInvitationsProvider>
        <LlmAgentProvider
          llmManager={runtime.llmManager}
          eventBus={{
            emit: (e, p) => runtime.emit(e, p),
            on: (e, cb) => runtime.on(e, cb),
          }}
        >
        <LlmConsentProvider llmManager={runtime.llmManager}>
        <React.Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--muted)' }}>Loading...</div>}>
        <Routes>
          <Route path="/about" element={<WelcomeLanding />}/>
          <Route path="/manifesto" element={<Manifesto />}/>
          <Route path="/privacy" element={<PrivacyPolicy />}/>
          <Route path="/terms" element={<TermsOfService />}/>
          <Route path="/auth/callback" element={<AuthCallbackPage />}/>
          <Route path="/auth/totp" element={<TotpLoginPage />}/>
          <Route path="/pair" element={<AppContent />}/>
          <Route path="/login" element={<Navigate to="/home" replace/>}/>
          <Route path="/signin" element={<Navigate to="/home" replace/>}/>
          <Route path="/" element={<Navigate to="/home" replace/>}/>
          <Route path="/account" element={<Navigate to="/settings?section=profiles" replace />}/>
          <Route path="/workspaces" element={<Navigate to="/settings?section=workspaces" replace />}/>
          <Route path="/devices" element={<Navigate to="/settings?section=devices" replace />}/>
          <Route path="/settings" element={<AppContent />}/>
          <Route path="/habits" element={<AppContent />}/>
          <Route path="/calendar" element={<AppContent />}/>
          <Route path="/events" element={<AppContent />}/>
          <Route path="/events/*" element={<AppContent />}/>
          <Route path="/trips" element={<AppContent />}/>
          <Route path="/trips/*" element={<AppContent />}/>
          <Route path="/chat" element={<AppContent />}/>
          <Route path="/chat/*" element={<AppContent />}/>
          <Route path="/meal-planner" element={<AppContent />}/>
          <Route path="/meal-planner/*" element={<AppContent />}/>
          <Route path="/community-votes" element={<AppContent />}/>
          <Route path="/community-votes/*" element={<AppContent />}/>
          <Route path="/docs/*" element={<AppContent />}/>
          <Route path="/training" element={<AppContent />}/>
          <Route path="/training/*" element={<AppContent />}/>
          <Route path="/contacts" element={<AppContent />}/>
          <Route path="/contacts/*" element={<AppContent />}/>
          <Route path="/finance" element={<AppContent />}/>
          <Route path="/finance/*" element={<AppContent />}/>
          <Route path="/shopping-list" element={<AppContent />}/>
          <Route path="/shopping-list/*" element={<AppContent />}/>
          <Route path="/todo" element={<AppContent />}/>
          <Route path="/todo/*" element={<AppContent />}/>
          <Route path="/groups" element={<AppContent />}/>
          <Route path="/groups/*" element={<AppContent />}/>
          <Route path="/publish" element={<AppContent />}/>
          <Route path="/publish/*" element={<AppContent />}/>
          <Route path="/devtools" element={<AppContent />}/>
          <Route path="/devtools/*" element={<AppContent />}/>
          <Route path="/emoji" element={<AppContent />}/>
          <Route path="/emoji/*" element={<AppContent />}/>
          <Route path="/admin" element={<AppContent />}/>
          <Route path="/admin/*" element={<AppContent />}/>
          <Route path="/home" element={<AppContent />}/>
          <Route path="*" element={<Navigate to="/home" replace/>}/>
        </Routes>
        </React.Suspense>
        </LlmConsentProvider>
        </LlmAgentProvider>
        </DmInvitationsProvider>
      </PendingDevicesProvider>
    </AuthGuard>
    </ProfileProvider>
    </AppRegistryProvider>
    </AccountProvider>
    </CountryProvider>
    </ThemeProvider>
    </FontSizeProvider>
    </BrowserRouter>);
};
